import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { ActivityStats } from 'types/interfaces/activity';
import './LivePulseDashboard.css';

interface LivePulseDashboardProps {
  stats: ActivityStats | undefined;
  isLoading: boolean;
  isConnected: boolean;
  /**
   * Feed pause state (Asana 1219008751039721, decision 11): Pause lives with the
   * LIVE indicator on Activity. Optional: the Feed page renders the strip alone.
   */
  isPaused?: boolean;
  togglePause?: () => void;
  pendingCount?: number;
  /** The Affordable feed is a refetched snapshot the pause buffer never sees; the button is inert there. */
  pauseDisabled?: boolean;
}

const formatStatNumber = (num: number): string => {
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return String(num);
};

/**
 * A single live stat value. Flashes a soft highlight when its value changes so the
 * feed visibly "registers" activity. The first settled value (and any change while
 * loading) does NOT flash, so the initial load is calm.
 */
const StatValue: React.FC<{ value: number; isLoading: boolean }> = ({ value, isLoading }) => {
  const prevRef = useRef(value);
  const settledRef = useRef(false);
  const [bumped, setBumped] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (!settledRef.current) {
      settledRef.current = true;
      prevRef.current = value;
      return;
    }
    if (prevRef.current !== value) {
      prevRef.current = value;
      setBumped(true);
    }
  }, [value, isLoading]);

  return (
    <span
      className={`live-pulse-dashboard__stat-value${bumped ? ' live-pulse-dashboard__stat-value--bumped' : ''}`}
      onAnimationEnd={() => setBumped(false)}
    >
      {isLoading ? '—' : formatStatNumber(value)}
    </span>
  );
};

const LivePulseDashboard: React.FC<LivePulseDashboardProps> = ({
  stats,
  isLoading,
  isConnected,
  isPaused = false,
  togglePause,
  pendingCount = 0,
  pauseDisabled = false,
}) => {
  // Paused wins over the socket state: the amber dot and PAUSED label read the
  // same at a glance whether the socket is up or reconnecting underneath.
  const dotClass = isPaused ? 'paused' : isConnected ? 'connected' : 'disconnected';
  const label = isPaused ? 'Paused' : isConnected ? 'Live' : 'Reconnecting';
  return (
    <div className="live-pulse-dashboard" data-testid="live-pulse" data-paused={isPaused || undefined}>
      <div className="live-pulse-dashboard__indicator">
        <span className={`live-pulse-dashboard__dot live-pulse-dashboard__dot--${dotClass}`} />
        <span className="live-pulse-dashboard__label" data-testid="live-pulse-label">
          {label}
        </span>
      </div>

      <div className="live-pulse-dashboard__divider" />

      <div className="live-pulse-dashboard__stats">
        <div className="live-pulse-dashboard__stat">
          <StatValue value={stats?.tradesLast24h ?? 0} isLoading={isLoading} />
          <span className="live-pulse-dashboard__stat-label">trades/day</span>
        </div>

        <div className="live-pulse-dashboard__divider" />

        <div className="live-pulse-dashboard__stat">
          <StatValue value={stats?.packagesShippedToday ?? 0} isLoading={isLoading} />
          <span className="live-pulse-dashboard__stat-label">shipped today</span>
        </div>

        <div className="live-pulse-dashboard__divider" />

        <div className="live-pulse-dashboard__stat">
          <StatValue value={stats?.cardsTradedThisWeek ?? 0} isLoading={isLoading} />
          <span className="live-pulse-dashboard__stat-label">cards/wk</span>
        </div>
      </div>

      {togglePause && (
        <button
          className={`live-pulse-dashboard__pause${isPaused ? ' live-pulse-dashboard__pause--active' : ''}`}
          onClick={togglePause}
          type="button"
          disabled={pauseDisabled}
          title={pauseDisabled ? 'Live updates pause the event feed only' : undefined}
          aria-label={isPaused ? `Resume feed (${pendingCount} pending)` : 'Pause feed'}
          data-testid="live-pulse-pause"
        >
          <FontAwesomeIcon icon={isPaused ? faPlay : faPause} />
          <span className="live-pulse-dashboard__pause-text">{isPaused ? 'Resume' : 'Pause'}</span>
          {isPaused && pendingCount > 0 && (
            <span className="live-pulse-dashboard__pending-badge">{pendingCount}</span>
          )}
        </button>
      )}
    </div>
  );
};

export default LivePulseDashboard;
